/* istanbul ignore file */

import {CMS_SCRIPTS_MIME_TYPE} from '../../constants'
import {isJavascriptMimeType} from '../../utils/mime'
import {isServer} from '../../utils/utils'

const parse5Loader = () => import('parse5')

export const TYPE_CONTENT_SLOT = 2
export const TYPE_CONTENT_ASSET = 1

function walk(documentFragment, fn) {
    for (let node of documentFragment.childNodes) {
        fn(node)
        if (node.childNodes) {
            walk(node, fn)
        }
    }
}

const normalizeSlotContent = (slotContent) => ({
    c_body: slotContent
})

/**
 * Utility to load cms content inside page getProps. The result should be passed to
 * <CMS content={result} />
 * @param {string} cid CMS content ID
 * @param {object} api
 * @return {Promise<Object>}
 */
export const preloadCmsContent = async (cid, {api}, type = TYPE_CONTENT_ASSET) => {
    // load only on server, as the cms-content component will retry loading in the browser
    if (!isServer) {
        return
    }
    try {
        const rawContent =
            type === TYPE_CONTENT_ASSET
                ? await api.shopperContent.getContent({
                      parameters: {cid, html: true}
                  })
                : normalizeSlotContent(await api.sapi.getSlot({parameters: {slotId: cid}}))

        const {parseFragment, serialize} = await parse5Loader()

        const documentFragment = parseFragment(rawContent.c_body)

        const convertScript = (node) => {
            // ignore non script tags
            if (node.tagName?.toLowerCase() !== 'script') {
                return
            }
            node.attrs = node.attrs || []
            // ignore non javascript script tags
            if (
                node.attrs.some(({name, value}) => {
                    if (name !== 'type') return false
                    const validScript = isJavascriptMimeType(value)
                    return !validScript
                })
            ) {
                return
            }

            node.attrs = node.attrs.filter(({name}) => name !== 'type')
            node.attrs.push({
                name: 'type',
                value: CMS_SCRIPTS_MIME_TYPE
            })
        }

        const convertSkImage = (node) => {
            // if doesn't have .sk-image class, ignore
            if (
                !node.attrs?.some(
                    ({name, value}) => name === 'class' && value.split(' ').includes('sk-image')
                )
            ) {
                return
            }

            // walk the child nodes and convert data-srcset and data-src to srcset and src
            node.childNodes.forEach((child) => {
                child.attrs?.forEach((attr) => {
                    if (attr.name === 'data-srcset') {
                        attr.name = 'srcset'
                    }
                    if (attr.name === 'data-src') {
                        attr.name = 'src'
                    }
                    return attr
                })
            })
        }

        walk(documentFragment, (node) => {
            convertScript(node)
            convertSkImage(node)
        })

        rawContent.c_body = serialize(documentFragment)

        return rawContent
    } catch (e) {
        // prevent failures from breaking the page - the cms module will retry
        // loading in the browser and display appropriate error
        console.warn(`Failure while fetching cms module "${cid}"`, e)
    }
}
